import { createEffect, createSignal, For, onCleanup, Show } from "solid-js";
import type { PlayerId } from "../../core";
import { account, login, register, signOut } from "../../app/account";
import {
  fetchLeaderboard,
  fetchMyReplays,
  matchmakingCancel,
  matchmakingJoin,
  matchmakingPoll,
  type LeaderRow,
  type MatchStatus,
} from "../../app/api";
import type { ReplaySummary } from "../../shared/replay";
import { ThemeToggle } from "../components/ThemeToggle";
import { LobbyBoard } from "../components/LobbyBoard";
import { RulesOverlay } from "../components/RulesOverlay";
import { Icon } from "../components/Icon";
import { onboarded, markOnboarded } from "../../app/onboarding";

export interface Matched {
  readonly roomId: string;
  readonly seat: PlayerId;
  readonly token: string;
}

export function HomeScreen(props: {
  onVsComputer: () => void;
  onTutorial: () => void;
  onPlayFriend: () => void | Promise<void>;
  onMatched: (m: Matched) => void;
}) {
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [showRules, setShowRules] = createSignal(false);

  function openRules(): void {
    setShowRules(true);
    markOnboarded();
  }

  // --- auth modal ---
  const [authOpen, setAuthOpen] = createSignal(false);
  const [mode, setMode] = createSignal<"login" | "register">("register");
  const [username, setUsername] = createSignal("");
  const [password, setPassword] = createSignal("");
  const [authErr, setAuthErr] = createSignal<string | null>(null);
  const [authBusy, setAuthBusy] = createSignal(false);

  // --- leaderboard ---
  const [rows, setRows] = createSignal<LeaderRow[]>([]);
  const loadLeaderboard = async () => setRows(await fetchLeaderboard());
  createEffect(() => {
    account(); // re-load whenever the account (e.g. its rating) changes
    void loadLeaderboard();
  });

  // --- recent games (replays) ---
  const [recent, setRecent] = createSignal<ReplaySummary[]>([]);
  createEffect(() => {
    if (account()) void fetchMyReplays().then(setRecent);
    else setRecent([]);
  });

  // --- matchmaking ---
  const [searching, setSearching] = createSignal(false);
  const [waiting, setWaiting] = createSignal(0);
  let pollTimer: ReturnType<typeof setTimeout> | undefined;

  function stopSearch(): void {
    setSearching(false);
    if (pollTimer) clearTimeout(pollTimer);
    pollTimer = undefined;
  }
  onCleanup(stopSearch);

  function consume(s: MatchStatus): boolean {
    if (s.status === "matched" && s.roomId && s.seat != null && s.token) {
      stopSearch();
      props.onMatched({ roomId: s.roomId, seat: s.seat, token: s.token });
      return true;
    }
    if (typeof s.waiting === "number") setWaiting(s.waiting);
    return false;
  }

  function schedulePoll(): void {
    pollTimer = setTimeout(async () => {
      if (!searching()) return;
      try {
        if (consume(await matchmakingPoll())) return;
      } catch {
        /* transient — keep polling */
      }
      schedulePoll();
    }, 2000);
  }

  async function findGame(): Promise<void> {
    if (!account()) {
      setMode("login");
      setAuthOpen(true);
      return;
    }
    setError(null);
    setSearching(true);
    try {
      if (consume(await matchmakingJoin())) return;
      schedulePoll();
    } catch (e) {
      stopSearch();
      setError(e instanceof Error ? e.message : "Matchmaking failed.");
    }
  }

  function cancelSearch(): void {
    stopSearch();
    void matchmakingCancel();
  }

  async function playFriend(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await props.onPlayFriend();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not create game.");
    } finally {
      setBusy(false);
    }
  }

  async function submitAuth(): Promise<void> {
    const name = username().trim();
    if (!name || !password()) return;
    setAuthBusy(true);
    setAuthErr(null);
    const fn = mode() === "register" ? register : login;
    const r = await fn(name, password());
    setAuthBusy(false);
    if (r.error) {
      setAuthErr(r.error);
      return;
    }
    setAuthOpen(false);
    setUsername("");
    setPassword("");
  }

  return (
    <div class="app lobby-app">
      <div class="topbar">
        <div class="brand">
          <h1>Chessdeck</h1>
          <span class="tag">chess, but you build the army</span>
        </div>
        <div class="topbar-actions">
          <Show
            when={account()}
            fallback={
              <button class="btn btn-primary" onClick={() => { setMode("login"); setAuthOpen(true); }}>
                Sign in
              </button>
            }
          >
            {(acc) => (
              <div class="account-chip">
                <span class="avatar">{acc().username.charAt(0).toUpperCase()}</span>
                <span class="ac-meta">
                  <span class="ac-name">{acc().username}</span>
                  <span class="ac-rating">
                    {acc().rating} · {acc().wins}W {acc().losses}L
                  </span>
                </span>
                <button class="icon-link" title="Sign out" onClick={signOut}>
                  <Icon name="logout" />
                </button>
              </div>
            )}
          </Show>
          <button class="btn btn-ghost" onClick={openRules}>
            <Icon name="help" /> How to play
          </button>
          <ThemeToggle />
        </div>
      </div>

      <div class="lobby">
        <div class="lobby-board-wrap">
          <LobbyBoard />
        </div>

        <div class="lobby-right">
          <Show when={!onboarded()}>
            <div class="welcome-card">
              <div class="welcome-text">
                <strong>New to Chessdeck?</strong>
                <span>Build a deck, draw a card each turn, and checkmate the King.</span>
              </div>
              <div class="welcome-actions">
                <button class="btn btn-primary" onClick={openRules}>How to play</button>
                <button class="icon-link" title="Dismiss" onClick={markOnboarded}>
                  <Icon name="close" />
                </button>
              </div>
            </div>
          </Show>

          <div class="pairing">
            <button class="pmode pmode-primary" onClick={findGame}>
              <span class="pmode-icon"><Icon name="search" /></span>
              <span class="pmode-title">Find a game</span>
              <span class="pmode-sub">Ranked matchmaking — get paired with another player.</span>
            </button>

            <button class="pmode" onClick={props.onVsComputer}>
              <span class="pmode-icon"><Icon name="cpu" /></span>
              <span class="pmode-title">Play vs Computer</span>
              <span class="pmode-sub">Build a deck, pick a difficulty, face a bot.</span>
            </button>

            <button class="pmode" onClick={playFriend} disabled={busy()}>
              <span class="pmode-icon"><Icon name="link" /></span>
              <span class="pmode-title">{busy() ? "Creating…" : "Play a friend"}</span>
              <span class="pmode-sub">Share a private link and play a casual game.</span>
            </button>

            <button class="pmode" onClick={props.onTutorial}>
              <span class="pmode-icon"><Icon name="book" /></span>
              <span class="pmode-title">Tutorial</span>
              <span class="pmode-sub">A guided first game — deploy, take the Prince, mate the King.</span>
            </button>
          </div>

          <Show when={error()}>
            <div class="error">{error()}</div>
          </Show>

          <Show when={account() && recent().length > 0}>
            <div class="panel lb-panel">
              <h2 class="lb-head"><Icon name="book" /> Recent games</h2>
              <ol class="lb">
                <For each={recent()}>
                  {(g) => {
                    const me = account()?.username;
                    const verdict =
                      g.winner === null
                        ? "draw"
                        : (g.winner === 0 ? g.whiteName : g.blackName) === me
                          ? "won"
                          : "lost";
                    return (
                      <li class="lb-row">
                        <span class="lb-name">
                          <a href={`/r/${g.id}`} title="Watch the replay">
                            {g.whiteName} vs {g.blackName}
                          </a>
                        </span>
                        <span class="lb-rating">{verdict}</span>
                      </li>
                    );
                  }}
                </For>
              </ol>
            </div>
          </Show>

          <div class="panel lb-panel">
            <h2 class="lb-head"><Icon name="trophy" /> Leaderboard</h2>
            <Show
              when={rows().length > 0}
              fallback={<p class="budget-sub">No ranked players yet — be the first.</p>}
            >
              <ol class="lb">
                <For each={rows()}>
                  {(e, i) => (
                    <li class="lb-row" classList={{ you: e.id === account()?.id }}>
                      <span class="lb-rank">{i() + 1}</span>
                      <span class="lb-name">
                        {e.username}
                        {e.id === account()?.id ? " (you)" : ""}
                      </span>
                      <span class="lb-rating">{e.rating}</span>
                    </li>
                  )}
                </For>
              </ol>
            </Show>
          </div>
        </div>
      </div>

      <Show when={searching()}>
        <div class="overlay">
          <div class="overlay-card">
            <div class="spinner" />
            <h2>Finding a game…</h2>
            <p>{waiting() > 1 ? `${waiting()} players in the queue.` : "Waiting for an opponent."}</p>
            <div class="field-row" style={{ "justify-content": "center" }}>
              <button class="btn btn-ghost" onClick={cancelSearch}>Cancel</button>
            </div>
          </div>
        </div>
      </Show>

      <Show when={authOpen()}>
        <div class="overlay">
          <div class="overlay-card">
            <div class="big-glyph"><Icon name="user" /></div>
            <h2>{mode() === "register" ? "Create an account" : "Sign in"}</h2>
            <p>{mode() === "register" ? "Pick a username and password to play ranked." : "Welcome back."}</p>
            <div class="field" style={{ "max-width": "280px", margin: "0 auto 10px" }}>
              <input
                type="text"
                placeholder="username"
                autocomplete="username"
                value={username()}
                maxLength={20}
                onInput={(e) => setUsername(e.currentTarget.value)}
              />
            </div>
            <div class="field" style={{ "max-width": "280px", margin: "0 auto 12px" }}>
              <input
                type="password"
                placeholder="password"
                autocomplete={mode() === "register" ? "new-password" : "current-password"}
                value={password()}
                onInput={(e) => setPassword(e.currentTarget.value)}
                onKeyDown={(e) => e.key === "Enter" && void submitAuth()}
              />
            </div>
            <Show when={authErr()}>
              <div class="error" style={{ "max-width": "280px", margin: "0 auto 10px" }}>{authErr()}</div>
            </Show>
            <div class="field-row" style={{ "justify-content": "center", gap: "8px" }}>
              <button class="btn btn-ghost" onClick={() => setAuthOpen(false)}>Cancel</button>
              <button class="btn btn-primary" disabled={authBusy() || !username().trim() || !password()} onClick={() => void submitAuth()}>
                {authBusy() ? "…" : mode() === "register" ? "Create account" : "Sign in"}
              </button>
            </div>
            <p class="budget-sub" style={{ "margin-top": "12px" }}>
              {mode() === "register" ? "Already have an account? " : "New here? "}
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); setAuthErr(null); setMode(mode() === "register" ? "login" : "register"); }}
              >
                {mode() === "register" ? "Sign in" : "Create one"}
              </a>
            </p>
          </div>
        </div>
      </Show>

      <Show when={showRules()}>
        <RulesOverlay onClose={() => setShowRules(false)} />
      </Show>
    </div>
  );
}
